import { Component } from "react";
import { Button } from "@/components/ui/button";

/**
 * Captura errores de renderizado y evita pantalla en blanco total.
 * `inline`: variante compacta para paneles embebidos (no ocupa toda la ventana).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] Error inesperado en la interfaz:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const inline = Boolean(this.props.inline);
    if (error) {
      if (inline) {
        return (
          <div
            className="rounded-md border border-rose-200 bg-rose-50/40 px-3 py-4 text-sm space-y-2"
            data-testid="error-boundary-inline"
          >
            <p className="m-0 font-semibold text-rose-900">No se pudo mostrar este panel</p>
            <p className="m-0 text-xs text-slate-600">
              Intente de nuevo. Si continúa, recargue la aplicación.
            </p>
            {error?.message ? (
              <pre
                className="text-left text-[10px] text-rose-800 bg-white/80 p-2 rounded overflow-auto max-h-24 m-0"
                data-testid="error-boundary-message"
              >
                {String(error.message)}
              </pre>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-8" onClick={this.handleRetry}>
                Reintentar
              </Button>
              <Button type="button" size="sm" className="rounded-sm h-8" onClick={this.handleReload}>
                Recargar aplicación
              </Button>
            </div>
          </div>
        );
      }
      return (
        <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
          <div className="max-w-md w-full rounded-md border border-rose-200 bg-white p-6 shadow-sm text-center space-y-4">
            <h1 className="text-lg font-semibold text-rose-900">Error inesperado</h1>
            <p className="text-sm text-slate-600">
              Ocurrió un problema al mostrar esta pantalla. Contacta al administrador del sistema
              e indica la URL y la hora del incidente.
            </p>
            {error?.message ? (
              <pre
                className="text-left text-[10px] text-rose-800 bg-rose-50 p-2 rounded overflow-auto max-h-32"
                data-testid="error-boundary-message"
              >
                {String(error.message)}
              </pre>
            ) : null}
            <Button type="button" onClick={this.handleReload} className="w-full">
              Recargar aplicación
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
